'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';

interface ImageInfo {
  file: File;
  url: string;
  width: number;
  height: number;
}

export default function ImageCropTool() {
  const [image, setImage] = useState<ImageInfo | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropW, setCropW] = useState(0);
  const [cropH, setCropH] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const loadImage = useCallback((file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) {
      alert('Please upload a JPG, PNG, or WebP image.');
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      setImage({ file, url, width: img.naturalWidth, height: img.naturalHeight });
      setCropX(0);
      setCropY(0);
      setCropW(img.naturalWidth);
      setCropH(img.naturalHeight);
    };
    img.src = url;
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadImage(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadImage(file);
  };

  const handleCrop = async () => {
    if (!image) return;
    setProcessing(true);
    try {
      const img = new window.Image();
      img.src = image.url;
      await new Promise<void>((resolve) => { img.onload = () => resolve(); if (img.complete) resolve(); });
      const canvas = document.createElement('canvas');
      canvas.width = cropW;
      canvas.height = cropH;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
      const outputType = image.file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => { if (b) resolve(b); else reject(new Error('Failed')); }, outputType, 0.92);
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = outputType === 'image/png' ? 'png' : 'jpg';
      a.download = `cropped_${image.file.name.replace(/\.[^.]+$/, '')}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Crop failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  return (
    <div className="space-y-6">
      {!image ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload Image</p>
          <p className="text-text-muted text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-text-muted text-xs">Supports JPG, PNG, WebP</p>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileChange} />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-text-primary text-sm truncate max-w-[200px]">{image.file.name}</p>
            <button onClick={() => { setImage(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-text-muted hover:text-red-500 transition-colors text-xs">Remove</button>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 overflow-auto max-h-64 flex items-center justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={image.url} alt="Preview" className="max-w-full max-h-56 object-contain rounded" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-text-muted mb-1 block">X (left offset px)</label>
              <input type="number" value={cropX} onChange={(e) => setCropX(clamp(Number(e.target.value), 0, image.width - 1))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue" min={0} max={image.width - 1} />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Y (top offset px)</label>
              <input type="number" value={cropY} onChange={(e) => setCropY(clamp(Number(e.target.value), 0, image.height - 1))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue" min={0} max={image.height - 1} />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Width (px)</label>
              <input type="number" value={cropW} onChange={(e) => setCropW(clamp(Number(e.target.value), 1, image.width - cropX))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue" min={1} max={image.width - cropX} />
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Height (px)</label>
              <input type="number" value={cropH} onChange={(e) => setCropH(clamp(Number(e.target.value), 1, image.height - cropY))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue" min={1} max={image.height - cropY} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Square', fn: () => { const s = Math.min(image.width, image.height); setCropX(0); setCropY(0); setCropW(s); setCropH(s); } },
              { label: '16:9', fn: () => { const w = image.width; const h = Math.round(w * 9 / 16); setCropX(0); setCropY(0); setCropW(w); setCropH(Math.min(h, image.height)); } },
              { label: '4:3', fn: () => { const w = image.width; const h = Math.round(w * 3 / 4); setCropX(0); setCropY(0); setCropW(w); setCropH(Math.min(h, image.height)); } },
              { label: 'Full', fn: () => { setCropX(0); setCropY(0); setCropW(image.width); setCropH(image.height); } },
            ].map((p) => (
              <button key={p.label} onClick={p.fn} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">{p.label}</button>
            ))}
          </div>

          <div className="bg-surface rounded-xl p-3 text-sm text-text-secondary">
            Output: <span className="font-semibold text-text-primary">{cropW} × {cropH} px</span>
          </div>

          <button
            onClick={handleCrop}
            disabled={processing}
            className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Cropping…</>
            ) : (
              <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199M7.848 15.75l1.536-.887m-1.536.887a3 3 0 11-5.196 3 3 3 0 015.196-3zm1.536-.887a2.165 2.165 0 001.083-1.838c.005-.352.054-.695.14-1.025m-1.223 2.863l2.077-1.199m0-3.328a4.323 4.323 0 012.068-1.379l5.325-1.628a4.5 4.5 0 012.48-.044l.803.215-7.794 4.5m-2.882-1.664A4.331 4.331 0 0010.607 12m3.736 0l7.794 4.499-1.966 3.408a4.5 4.5 0 01-2.48-.044l-5.325-1.628a4.323 4.323 0 01-2.068-1.379M14.343 12l-2.882 1.664" /></svg>Crop & Download</>
            )}
          </button>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  );
}
